import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { RouterPath } from "../../assets/dictionary/RouterPath";
import DataService from "../../context/ProfilService";
import { toast } from "sonner";
import { trackEvent } from "../../lib/matomo";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import axios from "axios";
import myAppConfig from "../../config";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  User,
  CreditCard,
  Bell,
  Trash2,
  Save,
  Edit3,
  Shield,
  AlertTriangle,
  CalendarDays,
  Clock,
  Sparkles,
  Crown,
  ExternalLink,
  UserCircle2,
} from "lucide-react";

// ─── Plan config (identique à AlertsPage) ────────────────────────────────────
const PLAN_LIMITS = {
  Aucun: 0,
  Basique: 1,
  Standard: 5,
  Premium: 15,
  Pro: 20,
};

const PLAN_COLORS = {
  Aucun: "bg-muted text-muted-foreground",
  Basique: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  Standard: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  Premium: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  Pro: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

const PLAN_ICONS = {
  Aucun: null,
  Basique: null,
  Standard: null,
  Premium: <Sparkles className="w-3 h-3" />,
  Pro: <Crown className="w-3 h-3" />,
};

const PLAN_ACCENT = {
  Aucun: "bg-muted/10 text-muted-foreground",
  Basique: "bg-slate-500/10 text-slate-600 dark:text-slate-400",
  Standard: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  Premium: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  Pro: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
};

function PlanBadge({ plan }) {
  const colorClass = PLAN_COLORS[plan] ?? PLAN_COLORS.Basique;
  const icon = PLAN_ICONS[plan];
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${colorClass}`}>
      {icon}
      {plan}
    </span>
  );
}

function StatCard({ label, value, sub, icon: Icon, accentClass }) {
  return (
    <Card className="bg-card/80 glass-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <p className="text-sm text-muted-foreground">{label}</p>
          {Icon && (
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accentClass}`}>
              <Icon className="w-4 h-4" />
            </div>
          )}
        </div>
        <p className="text-xl font-bold text-foreground leading-tight">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, updateUser } = useUser();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email_notification: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email_notification: user.email_notification ?? true,
      });
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast.error("Le prénom et le nom sont obligatoires");
      return;
    }
    setIsLoading(true);
    try {
      await DataService.updateMyInfo(formData);
      await updateUser();
      setIsEditing(false);
      toast.success("Profil mis à jour avec succès");
      try { trackEvent("profile", "profile_updated"); } catch (_) {}
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour du profil");
      try { trackEvent("profile", "profile_update_error"); } catch (_) {}
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email_notification: user?.email_notification ?? true,
    });
    setIsEditing(false);
  };

  const handleDeleteProfile = async () => {
    if (deleteConfirmation !== "supprimer") {
      toast.error("Veuillez taper 'supprimer' pour confirmer");
      return;
    }
    setIsDeleting(true);
    try {
      await DataService.deleteProfile();
      toast.success("Profil supprimé avec succès");
      try { trackEvent("profile", "profile_deleted"); } catch (_) {}
      navigate(RouterPath.LOGIN);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression du profil");
      try { trackEvent("profile", "profile_delete_error"); } catch (_) {}
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setDeleteConfirmation("");
    }
  };

  const handleCustomerPortal = async () => {
    setIsPortalLoading(true);
    try {
      const { data } = await axios.post(
        myAppConfig.api.ENDPOINT + "/api/v1/payments/customer-portal",
        {},
        { withCredentials: true }
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Impossible d'accéder au portail client.");
      }
    } catch {
      toast.error("Erreur : Aucun abonnement en cours.");
    } finally {
      setIsPortalLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center app-page-gradient">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" />
          <p className="text-muted-foreground text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  const plan = user.type_abonnement ?? "Aucun";
  const planLimit = PLAN_LIMITS[plan] ?? 0;
  const accentClass = PLAN_ACCENT[plan] ?? PLAN_ACCENT.Basique;

  return (
    <div className="min-h-screen app-page-gradient">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        {/* Header */}
        <div className="pt-10 md:pt-0">
          <div className="flex items-center gap-2 mb-1">
            <UserCircle2 className="hidden sm:block w-5 h-5 text-primary" />
            <h1 className="text-4xl font-bold text-foreground">
              Mon <span className="text-primary">profil</span>
            </h1>
          </div>
          <p className="text-muted-foreground">
            Gérez vos informations personnelles et vos préférences.
          </p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            label="Abonnement actuel"
            value={plan}
            sub={planLimit > 0 ? `Jusqu'à ${planLimit} veille${planLimit > 1 ? "s" : ""}` : "Aucune veille disponible"}
            icon={CreditCard}
            accentClass={accentClass}
          />
          <StatCard
            label="Membre depuis"
            value={formatDate(user.created_at)}
            icon={CalendarDays}
            accentClass="bg-primary/10 text-primary"
          />
          <StatCard
            label="Dernière connexion"
            value={formatDate(user.last_connexion)}
            icon={Clock}
            accentClass="bg-green-500/10 text-green-600 dark:text-green-400"
          />
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* ── Col gauche ── */}
          <div className="space-y-4">

            {/* Informations personnelles */}
            <Card className="glass-card">
              <CardHeader className="pb-2">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                  <User className="w-4 h-4 text-primary" />
                  Informations personnelles
                </h3>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="first_name" className="text-foreground text-xs font-medium uppercase tracking-wide text-muted-foreground">Prénom</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => handleInputChange("first_name", e.target.value)}
                      disabled={!isEditing}
                      placeholder="Votre prénom"
                      className="border-border/50 bg-card/90"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="last_name" className="text-foreground text-xs font-medium uppercase tracking-wide text-muted-foreground">Nom</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => handleInputChange("last_name", e.target.value)}
                      disabled={!isEditing}
                      placeholder="Votre nom"
                      className="border-border/50 bg-card/90"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Email</Label>
                  <Input
                    id="email"
                    value={user.email}
                    disabled
                    className="bg-muted/30 text-muted-foreground border-border/30"
                  />
                </div>

                <div className="flex gap-2 pt-1">
                  {isEditing ? (
                    <>
                      <Button onClick={handleSave} disabled={isLoading} size="sm">
                        {isLoading
                          ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary-foreground mr-2" />
                          : <Save className="h-3.5 w-3.5 mr-2" />}
                        Sauvegarder
                      </Button>
                      <Button variant="outline" onClick={handleCancelEdit} size="sm">
                        Annuler
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" onClick={() => setIsEditing(true)} size="sm">
                      <Edit3 className="h-3.5 w-3.5 mr-2" />
                      Modifier
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Notifications */}
            <Card className="glass-card">
              <CardHeader className="pb-2">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                  <Bell className="w-4 h-4 text-primary" />
                  Préférences
                </h3>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between gap-4 py-1">
                  <div>
                    <p className="text-sm font-medium text-foreground">Notifications par email</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Recevoir les rapports de veille par email
                    </p>
                  </div>
                  <Switch
                    id="email_notification"
                    checked={formData.email_notification}
                    onCheckedChange={async (checked) => {
                      handleInputChange("email_notification", checked);
                      try {
                        await DataService.updateMyInfo({
                          email_notification: checked,
                          first_name: user.first_name,
                          last_name: user.last_name,
                        });
                        await updateUser();
                        toast.success("Préférence mise à jour");
                      } catch (error) {
                        console.error(error);
                        toast.error("Erreur lors de la mise à jour");
                        handleInputChange("email_notification", !checked);
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ── Col droite ── */}
          <div className="space-y-4">

            {/* Abonnement */}
            <Card className="glass-card">
              <CardHeader className="pb-2">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                  <CreditCard className="w-4 h-4 text-primary" />
                  Abonnement
                </h3>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Plan actuel */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border/30">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Plan actuel</p>
                    <PlanBadge plan={plan} />
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground mb-1">Veilles incluses</p>
                    <p className="text-sm font-semibold text-foreground">{planLimit}</p>
                  </div>
                </div>

                {plan === "Aucun" ? (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      Souscrivez à un plan pour créer des veilles et recevoir des rapports personnalisés.
                    </p>
                    <Button className="w-full" onClick={() => navigate(RouterPath.BILLING)}>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Choisir un plan
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleCustomerPortal}
                      disabled={isPortalLoading}
                    >
                      {isPortalLoading
                        ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-foreground mr-2" />
                        : <ExternalLink className="h-3.5 w-3.5 mr-2" />}
                      Gérer mon abonnement
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full text-muted-foreground text-xs"
                      onClick={() => navigate(RouterPath.BILLING)}
                    >
                      Voir tous les plans
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Zone dangereuse */}
            <Card className="glass-card border-destructive/20">
              <CardHeader className="pb-2">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-destructive">
                  <AlertTriangle className="w-4 h-4" />
                  Zone dangereuse
                </h3>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/5 border border-destructive/20">
                  <Shield className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    La suppression de votre compte est <strong className="text-foreground">irréversible</strong>.
                    Toutes vos données, veilles et historiques seront définitivement supprimés.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteDialog(true)}
                  className="w-full"
                  size="sm"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                  Supprimer mon compte
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Dialog suppression */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-background border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              Êtes-vous absolument sûr ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-foreground">
              Cette action ne peut pas être annulée. Elle supprimera définitivement votre compte et toutes vos données.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20 space-y-2">
            <p className="text-sm text-foreground">
              Pour confirmer, tapez{" "}
              <span className="font-mono font-bold text-destructive">supprimer</span>{" "}
              dans le champ ci-dessous :
            </p>
            <Input
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="supprimer"
              className="border-destructive/20 focus:border-destructive bg-background"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="text-foreground border-border hover:bg-accent">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProfile}
              disabled={deleteConfirmation !== "supprimer" || isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting
                ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-destructive-foreground mr-2" />
                : <Trash2 className="h-3.5 w-3.5 mr-2" />}
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
